import { App, type URLOpenListenerEvent } from "@capacitor/app";
import { isNative } from "./capacitor";

/**
 * The path prefix the iOS app claims as universal links (#381):
 * `https://run.pbranks.com/app/...`. Only this prefix is in the association
 * file, so the website's own routes (its Garmin callback included) never get
 * pulled into the app; the app drops the prefix to find the in-app route.
 */
export const APP_LINK_PREFIX = "/app";

/** `/app/auth/garmin` → `/auth/garmin`; `/app` → `/`; anything else unchanged. */
export function stripAppLinkPrefix(pathname: string): string {
  if (pathname === APP_LINK_PREFIX) return "/";
  if (pathname.startsWith(APP_LINK_PREFIX + "/")) return pathname.slice(APP_LINK_PREFIX.length);
  return pathname;
}

/**
 * Turns a deep link into an in-app path: the Android custom scheme
 * (`pbranks://pbranks.com/auth/garmin?code=…`) or the iOS universal link
 * (`https://run.pbranks.com/app/auth/garmin?code=…`, #381).
 */
export function pathFromDeepLink(url: string): string | null {
  try {
    const u = new URL(url);
    const path = stripAppLinkPrefix(u.pathname || "/");
    return path + u.search;
  } catch {
    return null;
  }
}

/** Subscribe to Capacitor `appUrlOpen`; returns an unsubscribe. No-op on the web. */
export function listenForDeepLinks(
  navigate: (path: string) => void,
): () => void {
  if (!isNative()) return () => {};
  const sub = App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
    const path = pathFromDeepLink(event.url);
    if (path) navigate(path);
  });
  return () => {
    void sub.then((s) => s.remove());
  };
}

/** Subscribe to app resume; returns an unsubscribe. No-op on the web. */
export function listenForResume(onResume: () => void): () => void {
  if (!isNative()) return () => {};
  const sub = App.addListener("appStateChange", ({ isActive }) => {
    if (isActive) onResume();
  });
  return () => {
    void sub.then((s) => s.remove());
  };
}

/**
 * The URL the app was cold-started with, as an in-app path, or null. Capacitor
 * only fires `appUrlOpen` for an intent that arrives while the WebView is
 * already running; when Android killed the app while the user was in Garmin
 * Connect, the callback comes back as the launch intent instead (#371).
 * No-op on the web.
 */
export async function launchDeepLinkPath(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const launch = await App.getLaunchUrl();
    return launch?.url ? pathFromDeepLink(launch.url) : null;
  } catch {
    return null;
  }
}
